import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  Renderer2,
  signal,
} from '@angular/core';
import { BehaviorSubject, combineLatest, map, Observable, of, switchMap, tap, Subject } from 'rxjs';
import { Stack } from 'src/app/common/dataStructures/stack';
import { PaginationOperationEnum } from 'src/app/components/table/table.component';
import { ToggleOption } from 'src/app/components/option-toggle/option-toggle.component';
import { JsonHalCollection, JsonHalCollectionPageInfo, JsonHalLinks } from 'src/app/models/api/jsonhal.model';
import { ITablePageInfo, jsonHalPageInfoToTablePageInfo } from 'src/app/models/componentInputs/tablePageInfo.interface';
import { PageWithFiltersEnum } from 'src/app/models/filters/pageWithFilters.enum';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { IPaginationNavigationModel } from 'src/app/models/paginationNavigation.model';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { ITableColumn } from 'src/app/models/tableColumns';
import { FiltersService } from 'src/app/services/filters/filters.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { AiFindingsFiltersModel } from 'src/app/models/filters/implementations/pageFilters/aiFindingsFilters.model';
import { AiFindingsHttpService } from 'src/app/services/httpServices/detection/aiFindingsHttpService/ai-findings-http.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActiveFiltersHeightService } from 'src/app/services/infra/activeFiltersHeight/active-filters-height.service';
import { SidePanelService } from 'src/app/features/side-panel/services/side-panel.service';
import { extractContinuationTokenFromURL } from 'src/app/common/exractContinuationTokenFromURL';
import { FindingThreadModel } from 'src/app/models/api/finding-thread.model';
import { AiFindingsThreadTableRowViewModel } from './ai-findings-thread-table-row-view-model';
import { aiFindingsThreadsTableColumns } from './ai-findings-threads-table-columns';
import { getAiServiceLogoPath } from 'src/app/common/aiServiceUtils';
import { owaspLlmToDisplayText, owaspAsiToDisplayText } from 'src/app/common/violationRulesUtils';
import { toLabelBinding } from 'src/app/models/api/common';
import { UnifiedPipelineService, skipFirstRun } from 'src/app/services/infra/unifiedPipeline/unified-pipeline.service';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { FindingModel, DetectionRuleSeverity } from 'src/app/models/api/finding.model';
import { AiService } from 'src/app/models/api/agentStep.model';
import { RSA_DEMO_FINDING_AI_SERVICES } from 'src/app/models/selectOptions/rsaDemoAiServices';

@Component({
  selector: 'app-enterprise-ai-threads-table',
  templateUrl: './enterprise-ai-threads-table.component.html',
  styleUrls: ['./enterprise-ai-threads-table.component.scss'],
})
export class EnterpriseAiThreadsTableComponent implements OnInit {
  public sourceToggle = input<ToggleOption>('option1');
  public totalItems = output<number>();

  public columns = signal<ITableColumn[]>(aiFindingsThreadsTableColumns);
  public findingsCurrentPage = signal<AiFindingsThreadTableRowViewModel[]>([]);
  public findingsCurrentPage$ = new BehaviorSubject<AiFindingsThreadTableRowViewModel[]>([]);
  public isLoading = signal<boolean>(true);
  public showNoData = signal<boolean>(false);
  public isFiltersMenuOpen = signal(false);
  public sortedLastFindingColumn$ = new BehaviorSubject<SortColumn | undefined>({
    column: aiFindingsThreadsTableColumns.find((column) => column.sortKey === 'lastFinding')!,
    isDesc: true,
  });
  // public readonly defaultPageSize = signal<number>(this._aiFindingsHttpService.defaultPageSize);
  public readonly defaultPageSize = signal<number>(50);
  public pageFilters = signal<AiFindingsFiltersModel>(
    this._filtersService.getFilters(PageWithFiltersEnum.AiFindings) as AiFindingsFiltersModel
  );
  public pageInfo = signal<ITablePageInfo>({
    number: 1,
    currSize: 0,
    maxSize: this.defaultPageSize(),
    totalItems: 0,
    totalPages: 1,
  });

  private _paginationNavigation = signal<IPaginationNavigationModel | null>(null);
  private _pageChange$: Subject<string> = new Subject<string>();
  private _sourceToggleChange$: Subject<ToggleOption> = new Subject<ToggleOption>();
  private readonly _httpClient = inject(HttpClient);
  private readonly _featureFlagsService = inject(FeatureFlagsService);
  private _rsaMockThreads: FindingThreadModel[] | null = null;
  private _iamDemoMockThreads: FindingThreadModel[] | null = null;
  private _isOnFirstPage = true;
  private readonly _rsaDemoServiceSet = new Set<string>([...RSA_DEMO_FINDING_AI_SERVICES, 'langchain']);

  constructor(
    private readonly _sidePanel: SidePanelService,
    private readonly _logger: LoggerService,
    private readonly _aiFindingsHttpService: AiFindingsHttpService,
    private readonly _filtersService: FiltersService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _activeFiltersHeightService: ActiveFiltersHeightService,
    private readonly _renderer: Renderer2,
    private readonly _el: ElementRef,
    private readonly _destroyRef: DestroyRef,
    private readonly _unifiedPipelineService: UnifiedPipelineService
  ) {
    effect(() => {
      this.findingsCurrentPage$.next(this.findingsCurrentPage());
    });

    // Effect to route source toggle changes through Subject (skip initial run)
    const onSourceToggleChange = skipFirstRun((currentToggle: ToggleOption) => {
      if (this._unifiedPipelineService.isEnabled()) {
        this._sourceToggleChange$.next(currentToggle);
      }
    });
    effect(() => onSourceToggleChange(this.sourceToggle()), { allowSignalWrites: true });

    // Stream for source toggle changes - uses switchMap to cancel previous requests
    this._sourceToggleChange$
      .pipe(
        tap(() => {
          this.findingsCurrentPage.set([]);
          this.isLoading.set(true);
          this._paginationNavigation.set(null);
        }),
        switchMap((currentToggle: ToggleOption) => {
          const filters = this.pageFilters().getSelectedFilters();
          const filtersWithSource = this._unifiedPipelineService.applySourceFilter(filters, currentToggle);
          if (this._isOnlyRsaServicesSelected(filtersWithSource)) {
            return of(this._emptyResponse());
          }
          const cleaned = this._stripRsaServicesFromFilters(filtersWithSource);
          return this._aiFindingsHttpService.startGetThreads(cleaned, this.pageFilters().sortedColumn$.value);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (response: JsonHalCollection<FindingThreadModel>) => this._handleAgentStepCollectionResponse(response),
        error: (err: HttpErrorResponse) => this._fetchRequestOnError(err),
      });
  }

  ngOnInit(): void {
    const { onUpdate$, sortedColumn$ } = this.pageFilters();
    // Stream 1: onUpdate + sort changes -> startGetThreads
    combineLatest([onUpdate$, sortedColumn$])
      .pipe(
        tap(() => {
          this.findingsCurrentPage.set([]);
          this.isLoading.set(true);
          this._isOnFirstPage = true;
        }),
        switchMap(([filters, sort]: [ISelectedFilters, SortColumn | undefined]) => {
          const filtersWithSource = this._unifiedPipelineService.applySourceFilter(filters, this.sourceToggle());
          if (this._isOnlyRsaServicesSelected(filtersWithSource)) {
            return of(this._emptyResponse());
          }
          const cleaned = this._stripRsaServicesFromFilters(filtersWithSource);
          return this._aiFindingsHttpService.startGetThreads(cleaned, sort);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (response: JsonHalCollection<FindingThreadModel>) => this._handleAgentStepCollectionResponse(response),
        error: (err: HttpErrorResponse) => this._fetchRequestOnError(err),
      });

    // Stream 2: page changes -> getThreadsByContinuationToken
    this._pageChange$
      .pipe(
        tap(() => {
          this.findingsCurrentPage.set([]);
          this.isLoading.set(true);
        }),
        switchMap((token: string) => {
          const sp = this.sourceToggle() === 'option2' ? 'inline' : undefined;
          return this._aiFindingsHttpService.getThreadsByContinuationToken(token, sp);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (response: JsonHalCollection<FindingThreadModel>) => this._handleAgentStepCollectionResponse(response),
        error: (err: HttpErrorResponse) => this._fetchRequestOnError(err),
      });

    this._activeFiltersHeightService.height$
      .pipe(
        tap((height: number) =>
          this._renderer.setStyle(this._el.nativeElement, '--applied-filters-height', `${height}px`, 1)
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  public onPagination(operation: PaginationOperationEnum) {
    if (this.isLoading()) {
      return;
    }

    const navigation = this._paginationNavigation();
    if (!navigation) return;

    if (operation == PaginationOperationEnum.next) {
      navigation.previousPage.push(navigation.currentPage);
    }

    const pageUrl = operation == PaginationOperationEnum.next ? navigation.nextPage : navigation.previousPage.pop();

    this._isOnFirstPage = operation !== PaginationOperationEnum.next && navigation.previousPage.size() === 0;

    const continuationToken = extractContinuationTokenFromURL(pageUrl ?? '', this._logger);
    if (continuationToken) {
      this._pageChange$.next(continuationToken);
    }
  }

  public rowSelected(selection: AiFindingsThreadTableRowViewModel): void {
    const sourcePipeline = this.sourceToggle() === 'option2' ? 'inline' : undefined;
    this._sidePanel.openFindingThread(selection.id, undefined, sourcePipeline);
  }

  public rowUnselected() {
    this._sidePanel.close();
  }

  private async _handleAgentStepCollectionResponse(response: JsonHalCollection<FindingThreadModel>) {
    this._handlePaginationUrls(response._links);
    this._handlePageInfo(response.page);

    const items = response._embedded.items;
    const viewModels = items?.length ? items.map((item) => this._toTableRowModel(item)) : [];
    this.showNoData.set(viewModels.length === 0);
    this._loadRsaMockThreads(viewModels);
  }

  private _fetchRequestOnError(error: HttpErrorResponse) {
    this.findingsCurrentPage.set([]);
    this.isLoading.set(false);
    this.showNoData.set(true);
    this._httpErrorHandlerService.handle(error);
  }

  private _handlePaginationUrls(links: JsonHalLinks) {
    const currentNavigation = this._paginationNavigation();

    if (!currentNavigation) {
      this._paginationNavigation.set({
        currentPage: links.self.href,
        nextPage: links.next?.href,
        previousPage: new Stack<string>(),
      } as IPaginationNavigationModel);
    } else {
      const updatedNavigation = {
        ...currentNavigation,
        currentPage: links.self.href,
        nextPage: links.next?.href,
      };
      this._paginationNavigation.set(updatedNavigation);
    }
  }

  private _handlePageInfo(pageInfo: JsonHalCollectionPageInfo) {
    this.totalItems.emit(pageInfo.totalItems);
    const tablePageInfo = jsonHalPageInfoToTablePageInfo(pageInfo, this.defaultPageSize());
    this.pageInfo.set(tablePageInfo);
  }

  private _toTableRowModel(thread: FindingThreadModel): AiFindingsThreadTableRowViewModel {
    return {
      rowId: thread.id,
      clickable: false,
      id: thread.id,
      name: thread.name,
      platformIcon: getAiServiceLogoPath(thread.service),
      platform: thread.service,
      avatarLabels: thread.actorNames,
      avatarLabelsDisabled: thread.actorNames && thread.actorNames[0] === 'N/A',
      agentNames: thread.agentNames,
      findingsBySeverity: thread.findingsBySeverity,
      firstActivity: thread.firstActivity,
      lastActivity: thread.lastActivity,
      lastFinding: thread.lastFinding,
      labels: (thread.labels ?? []).map(toLabelBinding),
      owasp: owaspLlmToDisplayText(thread.owasp ?? []),
      owaspAsi: owaspAsiToDisplayText(thread.owasp ?? []),
    };
  }

  private _stripRsaServicesFromFilters(filters: ISelectedFilters): ISelectedFilters {
    if (!filters.aiService?.length) {
      return filters;
    }
    const cleaned = filters.aiService.filter((s) => !this._rsaDemoServiceSet.has(s));
    return { ...filters, aiService: cleaned.length ? cleaned : undefined };
  }

  private _isOnlyRsaServicesSelected(filters: ISelectedFilters): boolean {
    if (!filters.aiService?.length) {
      return false;
    }
    return filters.aiService.every((s) => this._rsaDemoServiceSet.has(s));
  }

  private _emptyResponse(): JsonHalCollection<FindingThreadModel> {
    return {
      _embedded: { items: [] },
      _links: { self: { href: '' }, next: { href: '' } },
      page: { number: 1, size: 0, totalItems: 0, totalPages: 1 },
    } as JsonHalCollection<FindingThreadModel>;
  }

  private _loadRsaMockThreads(apiViewModels: AiFindingsThreadTableRowViewModel[]): void {
    if (!this._isOnFirstPage) {
      this._setThreadsPage(apiViewModels);
      return;
    }

    const rsaEnabled = this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.RsaDemoRuntimeBoundaries);
    const iamEnabled = this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.SpmIamDemoAmex);

    if (!rsaEnabled && !iamEnabled) {
      this._setThreadsPage(apiViewModels);
      return;
    }

    const rsaReady = !rsaEnabled || !!this._rsaMockThreads;
    const iamReady = !iamEnabled || !!this._iamDemoMockThreads;

    if (rsaReady && iamReady) {
      this._prependMockThreads(apiViewModels);
      return;
    }

    type FM = Record<string, FindingModel>;
    const loads: Observable<FindingModel[]>[] = [];

    if (rsaEnabled && !this._rsaMockThreads) {
      loads.push(
        this._httpClient.get<FM>('assets/mockData/rsaFindingsMockData.json').pipe(map((d) => Object.values(d)))
      );
    }
    if (iamEnabled && !this._iamDemoMockThreads) {
      loads.push(
        this._httpClient.get<FM>('assets/mockData/iamDemoFindingsMockData.json').pipe(map((d) => Object.values(d)))
      );
    }

    combineLatest(loads).subscribe({
      next: (results) => {
        let idx = 0;
        if (rsaEnabled && !this._rsaMockThreads) {
          this._rsaMockThreads = this._buildThreadsFromFindings(results[idx++]);
        }
        if (iamEnabled && !this._iamDemoMockThreads) {
          const threads = this._buildThreadsFromFindings(results[idx]);
          const now = new Date();
          const base = new Date(now);
          base.setMinutes(now.getMinutes() - 5);
          this._iamDemoMockThreads = threads.map((t, i) => {
            const ts = new Date(base.getTime() - i * 15000);
            return {
              ...t,
              firstActivity: ts,
              lastActivity: ts,
              lastFinding: ts,
            };
          });
        }
        this._prependMockThreads(apiViewModels);
      },
      error: () => this._setThreadsPage(apiViewModels),
    });
  }

  private _prependMockThreads(apiViewModels: AiFindingsThreadTableRowViewModel[]): void {
    const filters = this.pageFilters().getSelectedFilters();
    const selectedServices = new Set(filters.aiService ?? []);
    const hasServiceFilter = selectedServices.size > 0;
    const currentToggle = this.sourceToggle();
    const isUnified = this._unifiedPipelineService.isEnabled();

    let allMockThreads: FindingThreadModel[] = [];

    if (this._iamDemoMockThreads) {
      allMockThreads = [...allMockThreads, ...this._iamDemoMockThreads];
    }
    if (this._rsaMockThreads) {
      allMockThreads = [...allMockThreads, ...this._rsaMockThreads];
    }

    if (hasServiceFilter) {
      const hasDemoSelected = [...selectedServices].some((s) => this._rsaDemoServiceSet.has(s));
      if (!hasDemoSelected) {
        this._setThreadsPage(apiViewModels);
        return;
      }
      allMockThreads = allMockThreads.filter((t) => selectedServices.has(t.service));
    }

    const mockViewModels = allMockThreads.map((t) => this._toTableRowModel(t));
    const merged = [...mockViewModels, ...apiViewModels].sort(
      (a, b) => new Date(b.lastFinding).getTime() - new Date(a.lastFinding).getTime()
    );
    this._setThreadsPage(merged);
  }

  private _setThreadsPage(viewModels: AiFindingsThreadTableRowViewModel[]): void {
    this.showNoData.set(viewModels.length === 0);
    this.findingsCurrentPage.set(viewModels);
    this.isLoading.set(false);
  }

  private _buildThreadsFromFindings(findings: FindingModel[]): FindingThreadModel[] {
    const threadMap = new Map<string, FindingModel[]>();
    for (const finding of findings) {
      const threadId = finding.thread.id;
      if (!threadMap.has(threadId)) {
        threadMap.set(threadId, []);
      }
      threadMap.get(threadId)!.push(finding);
    }

    const threads: FindingThreadModel[] = [];
    for (const [threadId, threadFindings] of threadMap) {
      const first = threadFindings[0];
      const severities: Record<DetectionRuleSeverity, number> = {
        [DetectionRuleSeverity.informational]: 0,
        [DetectionRuleSeverity.low]: 0,
        [DetectionRuleSeverity.medium]: 0,
        [DetectionRuleSeverity.high]: 0,
        [DetectionRuleSeverity.critical]: 0,
      };
      const owaspSet = new Set<string>();
      const actors = new Set<string>();
      const agents = new Set<string>();
      let earliest = first.timestamp;
      let latest = first.timestamp;

      for (const f of threadFindings) {
        const sev = f.rule.severity as DetectionRuleSeverity;
        if (sev in severities) {
          severities[sev]++;
        }
        (f.rule.owasp ?? []).forEach((o) => owaspSet.add(o));
        if (f.mainActor?.displayName) {
          actors.add(f.mainActor.displayName);
        }
        const agent = f.relatedSteps?.[0]?.botDetails?.displayName;
        if (agent) {
          agents.add(agent);
        }
        if (f.timestamp < earliest) earliest = f.timestamp;
        if (f.timestamp > latest) latest = f.timestamp;
      }

      threads.push({
        id: threadId,
        name: first.relatedSteps?.[0]?.botDetails?.displayName ?? threadId,
        service: first.rule.service as AiService,
        actorNames: [...actors],
        agentNames: [...agents],
        findingsBySeverity: severities,
        firstActivity: earliest,
        lastActivity: latest,
        lastFinding: latest,
        labels: first.labelBindings?.map((lb) => lb.label) ?? [],
        owasp: [...owaspSet],
      });
    }

    return threads;
  }
}
